import { useState, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ServiceOffering } from '../../../types/backend';
import { useServiceOfferingsService } from '../services/serviceOfferingsService';
import type { ServiceOfferingCreateRequest, ServiceOfferingUpdateRequest } from '../services/serviceOfferingsService';

export interface ServiceOfferingsState {
  offerings: ServiceOffering[];
  isLoading: boolean;
  error: string | null;
  selected: ServiceOffering | null;
}

// Query keys for the service-offerings admin catalog (a single server-side list
// read). Writes update this cache by key so the list stays instant across the
// admin surface without a refetch.
export const serviceOfferingsKeys = {
  list: () => ['service-offerings', 'list'] as const,
};

export function useServiceOfferingsManagement() {
  const service = useServiceOfferingsService();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<ServiceOffering | null>(null);

  const query = useQuery({
    queryKey: serviceOfferingsKeys.list(),
    queryFn: async () => {
      const response = await service.listOfferings();
      return response.service_offerings;
    },
  });

  const { refetch } = query;
  const load = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const select = useCallback((offering: ServiceOffering | null) => {
    setSelected(offering);
  }, []);

  const setList = useCallback(
    (updater: (prev: ServiceOffering[]) => ServiceOffering[]) => {
      queryClient.setQueryData<ServiceOffering[]>(serviceOfferingsKeys.list(), (prev) => updater(prev ?? []));
    },
    [queryClient],
  );

  const createMutation = useMutation({
    mutationFn: (request: ServiceOfferingCreateRequest) => service.createOffering(request),
    onSuccess: (offering) => {
      setList((prev) => [...prev, offering]);
    },
  });
  const { mutateAsync: createAsync } = createMutation;
  const create = useCallback(
    (request: ServiceOfferingCreateRequest): Promise<ServiceOffering> => createAsync(request),
    [createAsync],
  );

  const updateMutation = useMutation({
    mutationFn: ({ id, request }: { id: string; request: ServiceOfferingUpdateRequest }) =>
      service.updateOffering(id, request),
    onSuccess: (updated, { id }) => {
      setList((prev) => prev.map((o) => (o.id === id ? updated : o)));
      setSelected((sel) => (sel?.id === id ? updated : sel));
    },
  });
  const { mutateAsync: updateAsync } = updateMutation;
  const update = useCallback(
    (id: string, request: ServiceOfferingUpdateRequest): Promise<ServiceOffering> => updateAsync({ id, request }),
    [updateAsync],
  );

  const publishMutation = useMutation({
    mutationFn: ({ id, version }: { id: string; version: string }) => service.publishOffering(id, version),
    onSuccess: (updated, { id }) => {
      setList((prev) => prev.map((o) => (o.id === id ? updated : o)));
      setSelected((sel) => (sel?.id === id ? updated : sel));
    },
  });
  const { mutateAsync: publishAsync } = publishMutation;
  const publish = useCallback(
    (id: string, version: string): Promise<ServiceOffering> => publishAsync({ id, version }),
    [publishAsync],
  );

  const deprecateMutation = useMutation({
    mutationFn: (id: string) => service.deprecateOffering(id),
    onSuccess: (updated, id) => {
      setList((prev) => prev.map((o) => (o.id === id ? updated : o)));
      setSelected((sel) => (sel?.id === id ? updated : sel));
    },
  });
  const { mutateAsync: deprecateAsync } = deprecateMutation;
  const deprecate = useCallback(
    (id: string): Promise<ServiceOffering> => deprecateAsync(id),
    [deprecateAsync],
  );

  const removeMutation = useMutation({
    mutationFn: (id: string) => service.deleteOffering(id),
    onSuccess: (_result, id) => {
      setList((prev) => prev.filter((o) => o.id !== id));
      setSelected((sel) => (sel?.id === id ? null : sel));
    },
  });
  const { mutateAsync: removeAsync } = removeMutation;
  const remove = useCallback(
    async (id: string): Promise<void> => {
      await removeAsync(id);
    },
    [removeAsync],
  );

  const state: ServiceOfferingsState = {
    offerings: query.data ?? [],
    isLoading: query.isFetching,
    error: query.error
      ? query.error instanceof Error
        ? query.error.message
        : 'Failed to load service offerings'
      : null,
    selected,
  };

  return { state, load, select, create, update, publish, deprecate, remove };
}
